import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useUserContext } from '../Components/StateContext/UserContext';
import RestService from '../Services/RestService';
import OKomButton from '../Components/OsloKommuneUI/OKomButton';
import { jwtDecode } from 'jwt-decode';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';

const MyUserDetailsView: React.FC = () => {
  const { user, setUserInfo, setDepartmentInfo, userToken, department, userRoles } = useUserContext();

  const [mobilePhone, setMobilePhone] = useState<string>(user?.Mobilephone || '');
  const [email, setEmail] = useState<string>(user?.Email || '');
  const [firstname, setFirstname] = useState<string>(user?.Firstname || '');
  const [lastname, setLastname] = useState<string>(user?.Lastname || '');
  const [departmentId, setDepartmentId] = useState<number>(user?.DepartmentId || 0);
  const [departments, setDepartments] = useState<Api.Department[]>([]);
  const [roles, setRoles] = useState<string[]>(userRoles || []);

  const isAdmin = roles.includes('admin');
  const firstnameEditable = isAdmin || !user?.Firstname || user.Firstname === 'FornavnMangler';
  const lastnameEditable = isAdmin || !user?.Lastname || user.Lastname === 'FornavnMangler';

  useEffect(() => {
    setMobilePhone(user?.Mobilephone || '');
    setEmail(user?.Email || '');
    setFirstname(user?.Firstname || '');
    setLastname(user?.Lastname || '');
    setDepartmentId(user?.DepartmentId || 0);
    setRoles(userRoles || []);
  }, [user, userToken]);

  useEffect(() => {
    (async () => {
      try {
        const res = await RestService.get<Api.Department[]>('/api/examregistration/GetDepartments');
        setDepartments(res);
      } catch {
        // silent — dropdown stays empty if departments can't be loaded
      }
    })();
  }, []);

  const saveChanges = async () => {
    try {
      const payload : Api.UpdateUserInfoModel = {
        UserId: 0,
        MobilePhone: mobilePhone,
        Email: email,
        FirstName: firstnameEditable ? firstname : undefined,
        LastName: lastnameEditable ? lastname : undefined,
        DepartmentId: departmentId > 0 ? departmentId : undefined,
      };

      var response = await RestService.post<Api.UpdateUserInfoModel,Api.User>('/api/user/UpdateMyInfo', payload);
      toast.success('Informasjonen er blitt lagret!');
      setUserInfo(response);
      if (response.DepartmentId) {
        const updatedDept = departments.find(d => d.Id === response.DepartmentId);
        if (updatedDept) setDepartmentInfo(updatedDept);
      }
    } catch (error) {
      console.error(error);
      toast.error(`Feil med lagring. ${error}`);
    }
  };

  return (
    <>
      <OKomBreadcrumb path='Min side'></OKomBreadcrumb>
      <div className="ods-margin-top-11-breakpoint-medium ods-grid ods-grid--gap ods-grid--center">
        <div className="ods-grid__column--12 ods-grid__column--6-breakpoint-medium">
          <div className="ods-status-card">
            <div className="ods-status-card__content">
              <h3 className="ods-status-card__heading">Personopplysninger</h3>
              <div className="ods-status-card__actions">
                <div className="ods-input">
                  <label className="ods-input__label">
                    Fornavn
                    <input
                      className="ods-input__input"
                      disabled={!firstnameEditable}
                      value={firstname}
                      onChange={(e) => setFirstname(e.target.value)}
                      type="text"
                      autoComplete="given-name"
                      placeholder="Fornavn"
                      style={!firstnameEditable ? { backgroundColor: '#dadada' } : undefined}
                    />
                  </label>
                </div>

                <div className="ods-input ods-margin-top-3 ods-margin-bottom-13">
                  <label className="ods-input__label">
                    Etternavn
                    <input
                      className="ods-input__input"
                      disabled={!lastnameEditable}
                      value={lastname}
                      onChange={(e) => setLastname(e.target.value)}
                      type="text"
                      autoComplete="family-name"
                      placeholder="Etternavn"
                      style={!lastnameEditable ? { backgroundColor: '#dadada' } : undefined}
                    />
                  </label>
                </div>


                <div className="ods-input ods-margin-top-3 ods-margin-bottom-3">
                  <label className="ods-input__label">
                    Avdeling
                    <div className="ods-select__select">
                      <select
                        value={departmentId}
                        onChange={(e) => setDepartmentId(parseInt(e.target.value, 10))}
                      >
                        <option value={0}>-- velg avdeling --</option>
                        {departments.map(d => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                      </select>
                    </div>
                  </label>
                </div>

                <div className="ods-input ods-margin-top-3">
                  <label className="ods-input__label">
                    Mobiltelefon
                    <input onChange={(e) => setMobilePhone(e.target.value)} className="ods-input__input" value={mobilePhone} type="text" autoComplete="on" placeholder="Mobiltelefon" />
                  </label>
                </div>

                <div className="ods-input ods-margin-top-3 ods-margin-bottom-8">
                  <label className="ods-input__label">
                    E-post
                    <input onChange={(e) => setEmail(e.target.value)} className="ods-input__input" value={email} type="text" autoComplete="on" placeholder="E-post" />
                  </label>
                </div>
              </div>
              <OKomButton
                buttonText='Lagre'
                size='medium'
                onClick={() => saveChanges()}
              />
            </div>
          </div>
        </div>

        <div className="ods-grid__column--12 ods-grid__column--6-breakpoint-medium">
          <div className="ods-status-card">
            <div className="ods-status-card__content">
              <h3 className="ods-status-card__heading">Roller og rettigheter</h3>
              <div className="ods-status-card__actions">


              <div className="ods-input ods-margin-bottom-11">
                  <div className='ods-text--size-india'>Brukerid:</div>
                  {userToken &&
                    <div>{jwtDecode(userToken)["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"]}</div>}
                </div>

                <div className="ods-input ods-margin-bottom-11">
                  <div className='ods-text--size-india'>Roller:</div>
                  {roles.map((role) => (
                    <div key={role} className="">{role}</div>
                  ))}
                </div>

                <div className="ods-input ods-margin-bottom-11">
                  <div className='ods-text--size-india'>Skole:</div>
                  <div>{department?.Name}</div>
                </div>

              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MyUserDetailsView;
